import './coapplicantRelationshipPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import { RelationshipEnum } from '../../../store/coapplicantStore/enums/RelationshipEnum';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { isCoapplicantPersisted },
    coapplicantRelationship,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantRelationship,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      coapplicantRelationshipValue: coapplicantRelationship,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<RelationshipEnum>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }
    const coapplicantRelationshipValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantRelationship) && isValidClickEvent(event),
    );

    setCoapplicantRelationship(coapplicantRelationshipValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { coapplicantRelationshipValue } = values;

    if (!coapplicantRelationshipValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-relationship-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.coapplicantRelationship.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.coapplicantRelationship.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.coapplicantRelationship.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.coapplicantRelationship.progress.root}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.coapplicantRelationship.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantRelationship.form.h1}>
                {t('CoapplicantRelationshipPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantRelationship.form.h2}>
                {t('CoapplicantRelationshipPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantRelationship.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={coapplicantRelationship}
                  name="coapplicantRelationshipValue"
                  options={[
                    {
                      label: t('CoapplicantRelationshipPage.spouse'),
                      value: RelationshipEnum.SPOUSE,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption1,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.relative'),
                      value: RelationshipEnum.RELATIVE,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption2,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.friend'),
                      value: RelationshipEnum.FRIEND,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption3,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.coworker'),
                      value: RelationshipEnum.COWORKER,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption4,
                    },
                    {
                      label: t('CoapplicantRelationshipPage.other'),
                      value: RelationshipEnum.OTHER,
                      testId:
                        locator.pages.coapplicantRelationship.form.radioOption5,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantRelationship.form.radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.coapplicantRelationship.form.submitButton
                  }
                  disabled={isEmpty(coapplicantRelationship)}
                  loading={isSyncingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('CoapplicantRelationshipPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantRelationship.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantRelationshipPage';
